import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useCreatePlayTest,
  useCreateTopicMessage,
  useGamePlayTest,
  useGamePlayTestMetaData,
  useGamePlayTests,
  useGameRules,
  useTopicMessages,
  useUpdatePlayTest,
} from "@seabrookstudios/pitch2table";
import { useSnackbar } from "notistack";
import { GameImportPlayTestScoreMetadata } from "./GameImportPlaytestScoreMetadata";
import { GameImportPlaytestPhotoUpload } from "./GameImportPlaytestPhotoUpload";
import {
  buildNestiFyzPlaytestBody,
  buildNestiFyzPlaytestFeedbackBody,
  buildNestiFyzPlaytestOutcomes,
  findExistingImportedFeedback,
  findExistingImportedPlaytest,
  findNestiFyzScoreMetadata,
  getImportableFeedbacks,
  getImportablePlaytestPhotos,
  getImportablePlaytests,
  getPlaytestDateMillis,
  getPlaytestDurationMinutes,
  NESTIFYZ_PLAY_TEST_AUDIENCE_MAP,
  NESTIFYZ_PLAYTEST_MEDIUM_MAP,
  resolveImportedRulesVersion,
} from "./playtestImportUtils";

/**
 * @param {object} props
 * @param {() => void} props.onComplete
 */
const CompleteOnMount = ({ onComplete }) => {
  const hasCalled = useRef(false);

  useEffect(() => {
    if (hasCalled.current) {
      return;
    }

    hasCalled.current = true;
    onComplete();
  }, [onComplete]);

  return null;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").TopicId} props.topicId
 * @param {import("./parseExport").NestiFyzPlaytestFeedback} props.feedback
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const ImportPlaytestFeedbackMessage = ({ topicId, feedback, onComplete, onError }) => {
  const createTopicMessage = useCreateTopicMessage(topicId);
  const hasStarted = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;

    createTopicMessage
      .mutateAsync({
        body: buildNestiFyzPlaytestFeedbackBody(feedback),
        implementation: "Markdown",
      })
      .then(() => {
        onComplete();
      })
      .catch(() => {
        enqueueSnackbar("An error occurred importing play test feedback", { variant: "error" });
        onError();
      });
  }, [createTopicMessage, feedback, onComplete, onError, enqueueSnackbar]);

  return null;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").TopicId} props.topicId
 * @param {import("./parseExport").NestiFyzPlayTest} props.playTest
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const ImportPlaytestFeedbacks = ({ topicId, playTest, onComplete, onError }) => {
  const { data: messages = [], isLoading } = useTopicMessages(topicId);
  const [feedbackIndex, setFeedbackIndex] = useState(0);

  const feedbacks = useMemo(
    () => getImportableFeedbacks(playTest).filter((feedback) => !findExistingImportedFeedback(messages, feedback)),
    [messages, playTest]
  );

  if (isLoading) {
    return null;
  }

  if (feedbacks.length === 0) {
    return <CompleteOnMount onComplete={onComplete} />;
  }

  const currentFeedback = feedbacks[feedbackIndex];

  if (!currentFeedback) {
    return <CompleteOnMount onComplete={onComplete} />;
  }

  const advance = () => {
    if (feedbackIndex + 1 >= feedbacks.length) {
      onComplete();
      return;
    }

    setFeedbackIndex((index) => index + 1);
  };

  return (
    <ImportPlaytestFeedbackMessage
      key={feedbackIndex}
      topicId={topicId}
      feedback={currentFeedback}
      onComplete={advance}
      onError={onError}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestId} props.playTestId
 * @param {import("./parseExport").NestiFyzPlayTest} props.playTest
 * @param {(index: number) => void} props.onItemStart
 * @param {(index: number) => void} props.onItemComplete
 * @param {(index: number) => void} props.onItemSkip
 * @param {(index: number) => void} props.onItemError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const ImportPlaytestPhotos = ({
  gameId,
  playTestId,
  playTest,
  onItemStart,
  onItemComplete,
  onItemSkip,
  onItemError,
  onComplete,
  onError,
}) => {
  const photos = useMemo(() => getImportablePlaytestPhotos(playTest), [playTest]);

  if (photos.length === 0) {
    return <CompleteOnMount onComplete={onComplete} />;
  }

  return (
    <GameImportPlaytestPhotoUpload
      gameId={gameId}
      playTestId={playTestId}
      files={photos}
      onItemStart={onItemStart}
      onItemComplete={onItemComplete}
      onItemSkip={onItemSkip}
      onItemError={onItemError}
      onComplete={onComplete}
      onError={onError}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestId} props.playTestId
 * @param {import("@seabrookstudios/pitch2table-core").TopicId|undefined} props.topicId
 * @param {import("./parseExport").NestiFyzPlayTest} props.playTest
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentId|undefined} props.scoreMetadataId
 * @param {boolean} props.skipOutcomes
 * @param {(index: number) => void} props.onPhotoStart
 * @param {(index: number) => void} props.onPhotoComplete
 * @param {(index: number) => void} props.onPhotoSkip
 * @param {(index: number) => void} props.onPhotoError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const FinalizeImportedPlayTest = ({
  gameId,
  playTestId,
  topicId,
  playTest,
  scoreMetadataId,
  skipOutcomes,
  onPhotoStart,
  onPhotoComplete,
  onPhotoSkip,
  onPhotoError,
  onComplete,
  onError,
}) => {
  const [phase, setPhase] = useState(
    /** @type {"outcomes"|"feedbacks"|"photos"} */ (skipOutcomes ? "feedbacks" : "outcomes")
  );
  const { data: playTestData, isLoading: isPlayTestLoading } = useGamePlayTest(gameId, playTestId);
  const resolvedTopicId = topicId || playTestData?.topicId;
  const hasFeedbacks = getImportableFeedbacks(playTest).length > 0;

  if (phase === "outcomes") {
    return (
      <SetImportedPlayTestOutcomes
        gameId={gameId}
        playTestId={playTestId}
        playTest={playTest}
        scoreMetadataId={scoreMetadataId}
        onComplete={() => {
          setPhase("feedbacks");
        }}
        onError={onError}
      />
    );
  }

  if (phase === "feedbacks") {
    if (isPlayTestLoading) {
      return null;
    }

    if (!resolvedTopicId || !hasFeedbacks) {
      return (
        <CompleteOnMount
          onComplete={() => {
            setPhase("photos");
          }}
        />
      );
    }

    return (
      <ImportPlaytestFeedbacks
        topicId={resolvedTopicId}
        playTest={playTest}
        onComplete={() => {
          setPhase("photos");
        }}
        onError={onError}
      />
    );
  }

  return (
    <ImportPlaytestPhotos
      gameId={gameId}
      playTestId={playTestId}
      playTest={playTest}
      onItemStart={onPhotoStart}
      onItemComplete={onPhotoComplete}
      onItemSkip={onPhotoSkip}
      onItemError={onPhotoError}
      onComplete={onComplete}
      onError={onError}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestId} props.playTestId
 * @param {import("./parseExport").NestiFyzPlayTest} props.playTest
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentId|undefined} props.scoreMetadataId
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const SetImportedPlayTestOutcomes = ({ gameId, playTestId, playTest, scoreMetadataId, onComplete, onError }) => {
  const hasStarted = useRef(false);
  const updatePlayTest = useUpdatePlayTest(gameId, playTestId);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;

    const outcomes = buildNestiFyzPlaytestOutcomes(playTest.participants, scoreMetadataId);

    updatePlayTest
      .mutateAsync({
        date: undefined,
        audience: undefined,
        body: undefined,
        complete: undefined,
        duration: undefined,
        ignore: undefined,
        medium: undefined,
        outcomes,
        nonPlayerOutcomes: undefined,
        rulesVersion: undefined,
        timerStarted: undefined,
        timerRunning: undefined,
        // @ts-expect-error - implementation omitted when only updating outcomes
        implementation: undefined,
      })
      .then(() => {
        onComplete();
      })
      .catch(() => {
        enqueueSnackbar("An error occurred saving play test results", { variant: "error" });
        onError();
      });
  }, [onComplete, onError, playTest.participants, scoreMetadataId, updatePlayTest, enqueueSnackbar]);

  return null;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("./parseExport").NestiFyzGame} props.nestifyzGame
 * @param {import("./parseExport").NestiFyzPlayTest} props.playTest
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentId|undefined} props.scoreMetadataId
 * @param {(index: number) => void} props.onPhotoStart
 * @param {(index: number) => void} props.onPhotoComplete
 * @param {(index: number) => void} props.onPhotoSkip
 * @param {(index: number) => void} props.onPhotoError
 * @param {() => void} props.onStart
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const CreateImportedPlayTest = ({
  gameId,
  nestifyzGame,
  playTest,
  scoreMetadataId,
  onPhotoStart,
  onPhotoComplete,
  onPhotoSkip,
  onPhotoError,
  onStart,
  onComplete,
  onError,
}) => {
  const { data: playTests = [] } = useGamePlayTests(gameId);
  const { data: rules = [] } = useGameRules(gameId);
  const [playTestId, setPlayTestId] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PlayTestId|null} */ (null)
  );
  const [topicId, setTopicId] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").TopicId|undefined} */ (undefined)
  );
  const [skipOutcomes, setSkipOutcomes] = useState(false);
  const hasStarted = useRef(false);
  const createPlayTest = useCreatePlayTest(gameId);
  const { enqueueSnackbar } = useSnackbar();

  const rulesVersion = resolveImportedRulesVersion(nestifyzGame.rulebooks, rules, playTest.rulebookId);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    const existingPlayTest = findExistingImportedPlaytest(playTests, playTest);

    if (existingPlayTest) {
      hasStarted.current = true;
      onStart();
      setPlayTestId(existingPlayTest.id);
      setTopicId(existingPlayTest.topicId);
      setSkipOutcomes((existingPlayTest.outcomes || []).length > 0);
      return;
    }

    hasStarted.current = true;
    onStart();

    createPlayTest
      .mutateAsync({
        date: getPlaytestDateMillis(playTest),
        duration: getPlaytestDurationMinutes(playTest),
        complete: playTest.playthroughType === "Full",
        ignore: true,
        audience: playTest.guidedOrUnguided ? NESTIFYZ_PLAY_TEST_AUDIENCE_MAP[playTest.guidedOrUnguided] : undefined,
        medium: playTest.playtestType ? NESTIFYZ_PLAYTEST_MEDIUM_MAP[playTest.playtestType] : "Physical",
        rulesVersion,
        body: buildNestiFyzPlaytestBody(playTest),
        implementation: "Markdown",
        publisherId: undefined,
      })
      .then((createdPlayTest) => {
        if (!createdPlayTest?.id) {
          throw new Error("Play test was not created");
        }

        enqueueSnackbar("Play test imported", { variant: "success" });
        setPlayTestId(createdPlayTest.id);
        setTopicId(createdPlayTest.topicId);
      })
      .catch(() => {
        enqueueSnackbar("An error occurred importing play test", { variant: "error" });
        onError();
      });
  }, [createPlayTest, onError, onStart, playTest, playTests, rulesVersion, enqueueSnackbar]);

  if (!playTestId) {
    return null;
  }

  return (
    <FinalizeImportedPlayTest
      gameId={gameId}
      playTestId={playTestId}
      topicId={topicId}
      playTest={playTest}
      scoreMetadataId={scoreMetadataId}
      skipOutcomes={skipOutcomes}
      onPhotoStart={onPhotoStart}
      onPhotoComplete={onPhotoComplete}
      onPhotoSkip={onPhotoSkip}
      onPhotoError={onPhotoError}
      onComplete={onComplete}
      onError={onError}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("./parseExport").NestiFyzGame} props.nestifyzGame
 * @param {(index: number) => void} props.onItemStart
 * @param {(index: number) => void} props.onItemComplete
 * @param {(index: number) => void} props.onItemError
 * @param {(playtestIndex: number, photoIndex: number) => void} props.onPhotoStart
 * @param {(playtestIndex: number, photoIndex: number) => void} props.onPhotoComplete
 * @param {(playtestIndex: number, photoIndex: number) => void} props.onPhotoSkip
 * @param {(playtestIndex: number, photoIndex: number) => void} props.onPhotoError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const GameImportPlayTestProcessor = ({
  gameId,
  nestifyzGame,
  onItemStart,
  onItemComplete,
  onItemError,
  onPhotoStart,
  onPhotoComplete,
  onPhotoSkip,
  onPhotoError,
  onComplete,
  onError,
}) => {
  const { data: metadata = [], isLoading: isMetadataLoading } = useGamePlayTestMetaData(gameId);
  const { isLoading: isPlayTestsLoading } = useGamePlayTests(gameId);
  const { isLoading: isRulesLoading } = useGameRules(gameId);
  const sortedPlayTests = useMemo(() => getImportablePlaytests(nestifyzGame.playtests), [nestifyzGame.playtests]);
  const [playTestIndex, setPlayTestIndex] = useState(0);
  const currentPlayTest = sortedPlayTests[playTestIndex];
  const scoreMetadataId = findNestiFyzScoreMetadata(metadata)?.id;

  if (isMetadataLoading || isPlayTestsLoading || isRulesLoading) {
    return null;
  }

  if (!currentPlayTest) {
    return null;
  }

  const advance = () => {
    if (playTestIndex + 1 >= sortedPlayTests.length) {
      onComplete();
      return;
    }

    setPlayTestIndex((index) => index + 1);
  };

  return (
    <CreateImportedPlayTest
      key={playTestIndex}
      gameId={gameId}
      nestifyzGame={nestifyzGame}
      playTest={currentPlayTest}
      scoreMetadataId={scoreMetadataId}
      onPhotoStart={(photoIndex) => {
        onPhotoStart(playTestIndex, photoIndex);
      }}
      onPhotoComplete={(photoIndex) => {
        onPhotoComplete(playTestIndex, photoIndex);
      }}
      onPhotoSkip={(photoIndex) => {
        onPhotoSkip(playTestIndex, photoIndex);
      }}
      onPhotoError={(photoIndex) => {
        onPhotoError(playTestIndex, photoIndex);
      }}
      onStart={() => {
        onItemStart(playTestIndex);
      }}
      onComplete={() => {
        onItemComplete(playTestIndex);
        advance();
      }}
      onError={() => {
        onItemError(playTestIndex);
        onError();
      }}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("./parseExport").NestiFyzGame} props.nestifyzGame
 * @param {(index: number) => void} props.onItemStart
 * @param {(index: number) => void} props.onItemComplete
 * @param {(index: number) => void} props.onItemError
 * @param {(playtestIndex: number, photoIndex: number) => void} props.onPhotoStart
 * @param {(playtestIndex: number, photoIndex: number) => void} props.onPhotoComplete
 * @param {(playtestIndex: number, photoIndex: number) => void} props.onPhotoSkip
 * @param {(playtestIndex: number, photoIndex: number) => void} props.onPhotoError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
export const GameImportPlayTestUpload = ({
  gameId,
  nestifyzGame,
  onItemStart,
  onItemComplete,
  onItemError,
  onPhotoStart,
  onPhotoComplete,
  onPhotoSkip,
  onPhotoError,
  onComplete,
  onError,
}) => {
  const [metadataReady, setMetadataReady] = useState(false);

  if (!metadataReady) {
    return (
      <GameImportPlayTestScoreMetadata
        gameId={gameId}
        onComplete={() => {
          setMetadataReady(true);
        }}
        onError={onError}
      />
    );
  }

  return (
    <GameImportPlayTestProcessor
      gameId={gameId}
      nestifyzGame={nestifyzGame}
      onItemStart={onItemStart}
      onItemComplete={onItemComplete}
      onItemError={onItemError}
      onPhotoStart={onPhotoStart}
      onPhotoComplete={onPhotoComplete}
      onPhotoSkip={onPhotoSkip}
      onPhotoError={onPhotoError}
      onComplete={onComplete}
      onError={onError}
    />
  );
};
